import { TrophyIcon } from '@heroicons/react/24/solid'
import { useState, useEffect } from 'react'
import { statisticsAPI } from '../services/api'
import { UserStatistics } from '../types'

export default function StatisticsPage() {
  const [statistics, setStatistics] = useState<UserStatistics[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchStatistics()
  }, [])

  const fetchStatistics = async () => {
    try {
      setLoading(true)
      const response = await statisticsAPI.getUserStatistics()
      setStatistics(response.data.user_statistics)
    } catch (error) {
      console.error('Error fetching statistics:', error)
      alert('Failed to fetch statistics')
    } finally {
      setLoading(false)
    }
  }

  const getTrophyIcon = (rank: number) => {
    if (rank === 1) {
      return <TrophyIcon className="w-5 h-5 text-yellow-500" title="Champion" />
    } else if (rank === 2) {
      return <TrophyIcon className="w-5 h-5 text-gray-400" title="Runner-up" />
    }
    return null
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-gray-600 mt-4">Loading statistics...</p>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">User Statistics</h1>
        <p className="text-gray-600 mt-2">See how many images each user has annotated</p>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rank
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Images Annotated
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Keypoints
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Completed Projects
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {statistics.map(stat => (
              <tr key={stat.user_id} className={stat.rank <= 2 ? 'bg-gray-50' : ''}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-900 mr-2">#{stat.rank}</span>
                    {getTrophyIcon(stat.rank)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{stat.username}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-semibold text-gray-900">
                    {stat.images_annotated.toLocaleString()}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">
                    {stat.total_keypoints.toLocaleString()}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{stat.completed_projects}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
